/**
 * errorStore.ts
 *
 * Zustand store for persistent on-screen error notifications.
 * Errors are added when API calls or JS errors occur, and stay
 * visible until the user dismisses them — so they can screenshot
 * and share with support.
 *
 * Each error gets a short reference ID that also appears in
 * Sentry / App Insights so the support team can find the full trace.
 */
import { create } from 'zustand';

export interface AppError {
  /** Unique error instance ID */
  id: string;
  /** Customer-friendly title (e.g. "Connection Problem") */
  title: string;
  /** Customer-friendly description (first/primary message) */
  description: string;
  /** All backend messages — shown as a list when more than one */
  messages?: string[];
  /** True when this error carries backend-provided user-facing messages */
  isValidationError?: boolean;
  /** Optional action label */
  action?: string;
  /** Short reference code to share with support (e.g. "ERR-5A3F") */
  referenceId: string;
  /**
   * The X-Request-Id header sent on the outgoing HTTP request.
   * Search in App Insights: requests | where customDimensions.requestId == "..."
   */
  requestId?: string;
  /**
   * The traceId from ASP.NET ProblemDetails (Activity.Current.TraceId).
   * Search in App Insights: exceptions | where operation_Id == "..."
   */
  backendTraceId?: string;
  /** ISO timestamp when the error occurred */
  timestamp: string;
}

interface ErrorStore {
  errors: AppError[];
  addError: (title: string, description: string, action?: string) => string;
  addErrorWithId: (
    referenceId: string,
    title: string,
    description: string,
    action?: string,
    messages?: string[],
    isValidationError?: boolean,
    requestId?: string,
    backendTraceId?: string,
  ) => string;
  removeError: (id: string) => void;
  clearAll: () => void;
}

/** Generates a short reference ID — 8 uppercase hex chars for support tracing */
export function generateReferenceId(): string {
  const hex = Math.floor(Math.random() * 0xFFFFFF).toString(16).toUpperCase().padStart(6, '0');
  const ts = Date.now().toString(36).toUpperCase().slice(-3);
  return `${ts}${hex}`;
}

export const useErrorStore = create<ErrorStore>((set) => ({
  errors: [],

  addError: (title, description, action) => {
    const referenceId = generateReferenceId();
    const id = crypto.randomUUID();
    const error: AppError = { id, title, description, action, referenceId, timestamp: new Date().toISOString() };
    set((state) => ({ errors: [...state.errors, error] }));
    return referenceId;
  },

  addErrorWithId: (referenceId, title, description, action, messages, isValidationError, requestId, backendTraceId) => {
    const id = crypto.randomUUID();
    const error: AppError = {
      id, title, description, action, messages, isValidationError,
      requestId, backendTraceId,
      referenceId, timestamp: new Date().toISOString(),
    };
    set((state) => ({ errors: [...state.errors, error] }));
    return referenceId;
  },

  removeError: (id) =>
    set((state) => ({ errors: state.errors.filter((e) => e.id !== id) })),

  clearAll: () => set({ errors: [] }),
}));
